import {
  type DocumentReference,
  type Timestamp,
} from '@principle-theorem/shared';
import { type IManagementUser } from '../management/management-user';
import { type AddOnKey } from './add-on-key';
import { type AddOnTier } from './add-on-config';
import { type BillingMeterId } from '../organisation/metered-billing';

/**
 * Persisted override state on an addOn. Only the FORCE values are
 * stored; the absence of an `IAddOnOverride` IS the `Auto` case,
 * which is why there is no `Auto` member here (it is not a valid value to
 * write to Firestore).
 */
export enum AddOnOverrideState {
  ForceEnabled = 'forceEnabled',
  ForceDisabled = 'forceDisabled',
}

/**
 * Runtime-resolved override view used by `applyOverride`. Differs from
 * `AddOnOverrideState` by adding `Auto` to represent the no-override
 * (or expired-override) case, so callers can switch on a single tristate
 * instead of doing `if (override && !expired)` branching.
 */
export enum AddOnOverrideResolution {
  Auto = 'auto',
  ForceEnabled = 'forceEnabled',
  ForceDisabled = 'forceDisabled',
}

// Values map to Stripe Subscription.status; preserve Stripe's snake_case
// convention per the 3rd-party-mapping exception to the camelCase rule.
export enum AddOnBillingStatus {
  Active = 'active',
  PastDue = 'past_due',
  Unpaid = 'unpaid',
  Canceled = 'canceled',
}

/**
 * Management-applied override that takes precedence over the Stripe-derived
 * status. Stored on an add-on to turn off a paying customer's gate,
 * or enable a non-paying account for trials / customer recovery.
 *
 * `setBy` + `setAt` + `reason` flow through to the audit log via the
 * subscription-write event trigger. `expiresAt` reverts the add-on to
 * Stripe-derived state at the given time without requiring a follow-up write
 * — `SubscriptionAddOns.enabledKeys` resolves expiry at read time.
 * The hourly cron also clears expired overrides for the audit trail.
 */
export interface IAddOnOverride {
  state: AddOnOverrideState;
  setBy: DocumentReference<IManagementUser>;
  setAt: Timestamp;
  reason?: string;
  expiresAt?: Timestamp;
}

/**
 * Stamped by a management clear so the subscription-write audit trigger can
 * attribute the clear to the operator who made it, with their reason. Without
 * it, a removed override is indistinguishable from an expiry.
 */
export interface IAddOnOverrideClear {
  clearedBy: DocumentReference<IManagementUser>;
  clearedAt: Timestamp;
  reason: string;
}

/**
 * One add-on slot inside an organisation's subscription. The
 * webhook handler writes one of these per Stripe subscription item; canceled
 * items are kept (not removed) so billing status is retained until
 * the organisation re-subscribes.
 */
export interface ISubscriptionAddOn {
  addOnKey: AddOnKey;
  stripeProductId: string;
  stripePriceId: string;
  stripeSubscriptionItemId: string;
  tier?: AddOnTier;
  billingStatus: AddOnBillingStatus;
  billingMeterId?: BillingMeterId;
  createdAt: Timestamp;
  /**
   * Billing-only input computed from the Stripe status; overrides do not
   * change this field. Use `SubscriptionAddOns.enabledKeys` to decide access,
   * including override expiry and bundle membership.
   */
  active: boolean;
  backfilledAt?: Timestamp;
  override?: IAddOnOverride;
  lastOverrideClear?: IAddOnOverrideClear;
}
